import React, {useState} from 'react'
import {toast} from 'react-toastify'
import {CustomModal} from '@/app/modules/widgets/components/CustomModal'
import {DiscoveredSubscription} from '@/app/modules/membership/api'
import {useSubscription} from '@/shared/utils'
import {getLogger} from '@/shared/utils/logUtils'
import {toastDefaultConfig} from '@/shared/config/toastConfig'

/**
 * Surface the currently-pending list from `useSubscription().discoveredSubscriptions`
 * as a modal with per-row Link buttons. Mounted inside SubscriptionProvider so
 * it is available on every private page (post-login auto-discovery triggers
 * it too, not just the manual button on the preferences page).
 *
 * Renders nothing when the list is empty. Never mentions the upstream lookup
 * system by name — users see "subscriptions found for your account".
 */
export const SubscriptionDiscoveryModal: React.FC = () => {
  const {
    discoveredSubscriptions,
    linkDiscoveredSubscription,
    clearDiscoveredSubscriptions,
    isDiscovering,
  } = useSubscription()
  const [busySkey, setBusySkey] = useState<string | null>(null)

  if (discoveredSubscriptions.length === 0) {
    return null
  }

  const handleLink = async (sub: DiscoveredSubscription) => {
    setBusySkey(sub.skey)
    try {
      await linkDiscoveredSubscription(sub.skey)
      toast.success(
        `Linked ${sub.product_name || 'subscription'} (•••${sub.subscription_id.slice(-4)})`,
        toastDefaultConfig
      )
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'We could not link this subscription. Please try again later.'
      getLogger().error('Failed to link discovered subscription:', err)
      toast.error(message, toastDefaultConfig)
    } finally {
      setBusySkey(null)
    }
  }

  return (
    <CustomModal
      title='Additional subscriptions found'
      closeModal={clearDiscoveredSubscriptions}
      buttons={[
        {
          text: 'Close',
          className: 'btn btn-light',
          onClick: clearDiscoveredSubscriptions,
        },
      ]}
    >
      <div>
        <p className='mb-3'>
          We found {discoveredSubscriptions.length === 1 ? 'a subscription' : 'subscriptions'}{' '}
          registered to your account that{' '}
          {discoveredSubscriptions.length === 1 ? 'is' : 'are'} not linked here yet. Select which{' '}
          {discoveredSubscriptions.length === 1 ? 'one' : 'ones'} you want to link.
        </p>
        <ul className='list-group'>
          {discoveredSubscriptions.map((sub) => {
            const lastFour = sub.subscription_id.slice(-4)
            const product = sub.product_name || 'OmicsBox subscription'
            const rowBusy = isDiscovering && busySkey === sub.skey
            return (
              <li
                key={sub.skey}
                className='list-group-item d-flex justify-content-between align-items-center'
              >
                <div>
                  <div>
                    <strong>{product}</strong>{' '}
                    <span className='text-muted'>(•••{lastFour})</span>
                  </div>
                  {sub.end_date && (
                    <div className='text-muted' style={{fontSize: '0.85em'}}>
                      Valid until {sub.end_date}
                    </div>
                  )}
                </div>
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => handleLink(sub)}
                  disabled={rowBusy || isDiscovering}
                >
                  {rowBusy ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm me-2'
                        role='status'
                        aria-hidden='true'
                      />
                      Linking...
                    </>
                  ) : (
                    'Link'
                  )}
                </button>
              </li>
            )
          })}
        </ul>
        <p className='mt-3 mb-0 text-muted' style={{fontSize: '0.85em'}}>
          You can dismiss this dialog and link any of these later from your subscription settings.
        </p>
      </div>
    </CustomModal>
  )
}
